import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/DatePicker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { FloatField, FloatWrapper } from "@/components/ui/FormField";
import { toast } from "sonner";
import { format } from "date-fns";
import { LogOut, Calculator, ChevronRight } from "lucide-react";
import {
  apiGetOffboardingRequests,
  apiFinalizeOffboardingRequest,
  apiCancelOffboardingHR,
  apiRejectOffboardingHR,
  apiEditOffboardingDates,
  OffboardingRequest,
} from "@/lib/api";

export function OffboardingPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedRequest, setSelectedRequest] = useState<OffboardingRequest | null>(null);
  const [panelMode, setPanelMode] = useState<'finalize' | 'cancel-hr' | 'edit-dates' | 'reject-hr' | null>(null);

  const [actualLastDay, setActualLastDay] = useState<string>("");
  const [hrNotes, setHrNotes] = useState("");
  const [cancelReason, setCancelReason] = useState("");

  const [checklist, setChecklist] = useState({
    revokeAccess: false,
    returnEquipment: false,
  });

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ["all-offboarding-requests"],
    queryFn: apiGetOffboardingRequests,
  });

  const statusColors: Record<string, string> = {
    pending_manager: "bg-amber-100 text-amber-700 border-amber-200",
    pending_hr: "bg-blue-100 text-blue-700 border-blue-200",
    active: "bg-purple-100 text-purple-700 border-purple-200",
    completed: "bg-green-100 text-green-700 border-green-200",
    rejected: "bg-red-100 text-red-700 border-red-200",
    revoked: "bg-slate-100 text-slate-600 border-slate-200",
  };

  const finalizeMutation = useMutation({
    mutationFn: async ({ status }: { status: "active" | "completed" }) => {
      if (!selectedRequest) return;
      if (!actualLastDay) throw new Error("Actual last day is required");
      return apiFinalizeOffboardingRequest(selectedRequest.id, actualLastDay, hrNotes, status);
    },
    onSuccess: () => {
      toast.success(t("common.saved"));
      queryClient.invalidateQueries({ queryKey: ["all-offboarding-requests"] });
      closePanel();
    },
    onError: (err: any) => toast.error(err.message || "Failed to finalize"),
  });

  const rejectHrMutation = useMutation({
    mutationFn: async () => {
      if (!selectedRequest) return;
      return apiRejectOffboardingHR(selectedRequest.id, cancelReason);
    },
    onSuccess: () => {
      toast.success("Exit request rejected.");
      queryClient.invalidateQueries({ queryKey: ["all-offboarding-requests"] });
      closePanel();
    },
    onError: (err: any) => toast.error(err.message || "Failed to reject request"),
  });

  const cancelHrMutation = useMutation({
    mutationFn: async () => {
      if (!selectedRequest) return;
      return apiCancelOffboardingHR(selectedRequest.id, cancelReason);
    },
    onSuccess: () => {
      toast.success("Exit request cancelled.");
      queryClient.invalidateQueries({ queryKey: ["all-offboarding-requests"] });
      closePanel();
    },
    onError: (err: any) => toast.error(err.message || "Failed to cancel request"),
  });

  const editDatesMutation = useMutation({
    mutationFn: async () => {
      if (!selectedRequest) return;
      return apiEditOffboardingDates(selectedRequest.id, actualLastDay, hrNotes);
    },
    onSuccess: () => {
      toast.success("Exit details updated.");
      queryClient.invalidateQueries({ queryKey: ["all-offboarding-requests"] });
      closePanel();
    },
    onError: (err: any) => toast.error(err.message || "Failed to update dates"),
  });

  function closePanel() {
    setSelectedRequest(null);
    setPanelMode(null);
    setActualLastDay("");
    setHrNotes("");
    setCancelReason("");
    setChecklist({ revokeAccess: false, returnEquipment: false });
  }

  function handleOpenFinalize(req: OffboardingRequest) {
    setSelectedRequest(req);
    setPanelMode('finalize');
    setActualLastDay(req.actual_last_day || req.preferred_last_day || "");
    setHrNotes(req.hr_notes || "");
    setChecklist({ revokeAccess: req.status === "completed", returnEquipment: req.status === "completed" });
  }

  function canHrActOnRequest(req: OffboardingRequest): boolean {
    if (!req.actual_last_day) return true;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const lastDay = new Date(req.actual_last_day); lastDay.setHours(0, 0, 0, 0);
    return today <= lastDay;
  }

  const allChecked = checklist.revokeAccess && checklist.returnEquipment;

  return (
    <div className="space-y-4">
      {isLoading ? (
        <div className="text-center py-10">{t("common.loading")}</div>
      ) : requests.length === 0 ? (
        <div className="py-10 text-center text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
          {t("offboardingModule.noActiveRequests")}
        </div>
      ) : (
        <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-border/60 bg-muted/30 hover:bg-muted/30">
                <TableHead className="pl-4 font-semibold text-xs text-muted-foreground uppercase tracking-wide">Employee</TableHead>
                <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">Preferred Last Day</TableHead>
                <TableHead className="hidden md:table-cell font-semibold text-xs text-muted-foreground uppercase tracking-wide">Actual Last Day</TableHead>
                <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">Status</TableHead>
                <TableHead className="hidden lg:table-cell font-semibold text-xs text-muted-foreground uppercase tracking-wide">Reason</TableHead>
                <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">Actions</TableHead>
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map(req => {
                const canAct = canHrActOnRequest(req);
                const initials = (req.employee_name || "?")
                  .split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase();
                const isActive = req.status !== "completed";
                return (
                  <TableRow
                    key={req.id}
                    className="border-b border-border/40 transition-colors cursor-pointer hover:bg-muted/40"
                    onClick={() => isActive ? handleOpenFinalize(req) : undefined}
                  >
                    <TableCell className="pl-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary flex-shrink-0">
                          {initials}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{req.employee_name || `Employee #${req.employee_id}`}</p>
                          {req.employee_job_title && <p className="text-xs text-muted-foreground">{req.employee_job_title}</p>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-3 text-sm">
                      {format(new Date(req.preferred_last_day), "dd MMM yyyy")}
                    </TableCell>
                    <TableCell className="hidden md:table-cell py-3 text-sm text-muted-foreground">
                      {req.actual_last_day ? format(new Date(req.actual_last_day), "dd MMM yyyy") : "—"}
                    </TableCell>
                    <TableCell className="py-3">
                      <Badge className={`text-xs border ${statusColors[req.status] ?? "bg-muted text-muted-foreground"}`} variant="outline">
                        {t(`offboardingModule.status.${req.status}`)}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell py-3 text-sm text-muted-foreground max-w-[180px] truncate">
                      {req.reason || "—"}
                    </TableCell>
                    <TableCell className="py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex gap-2 items-center">
                        {isActive && (
                          <>
                            <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 text-xs"
                              onClick={() => { setSelectedRequest(req); setPanelMode('reject-hr'); setCancelReason(''); }}>
                              Reject
                            </Button>
                            <Button size="sm" variant="outline" className="text-brand border-brand/30 hover:bg-brand/10 hover:text-brand text-xs"
                              onClick={() => handleOpenFinalize(req)}>
                              {t("offboardingModule.finalize")}
                            </Button>
                          </>
                        )}
                        {!isActive && canAct && (
                          <>
                            <Button size="sm" variant="outline" className="text-xs"
                              onClick={() => { setSelectedRequest(req); setPanelMode('edit-dates'); setActualLastDay(req.actual_last_day || ""); setHrNotes(req.hr_notes || ""); }}>
                              Edit Dates
                            </Button>
                            <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 text-xs"
                              onClick={() => { setSelectedRequest(req); setPanelMode('cancel-hr'); }}>
                              Cancel Exit
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-3 pr-3">
                      <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!selectedRequest} onOpenChange={(val) => !val && closePanel()}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogOut className="h-5 w-5" />
              {panelMode === 'finalize' && `${t("offboardingModule.finalize")} — ${selectedRequest?.employee_name}`}
              {panelMode === 'cancel-hr' && `Cancel Exit — ${selectedRequest?.employee_name}`}
              {panelMode === 'reject-hr' && `Reject Exit Request — ${selectedRequest?.employee_name}`}
              {panelMode === 'edit-dates' && `Edit Exit Dates — ${selectedRequest?.employee_name}`}
            </DialogTitle>
          </DialogHeader>

          {panelMode === 'reject-hr' && (
            <>
              <div className="py-2">
                <p className="text-sm text-muted-foreground mb-2">This will reject the employee's pending exit request.</p>
                <FloatField
                  label="Reason for Rejection"
                  required
                  type="textarea"
                  value={cancelReason}
                  onChange={(v) => setCancelReason(v as string)}
                  placeholder="Explain why…"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closePanel}>Cancel</Button>
                <Button variant="destructive" onClick={() => rejectHrMutation.mutate()} disabled={rejectHrMutation.isPending || !cancelReason.trim()}>
                  {rejectHrMutation.isPending ? t("common.saving") : "Confirm Rejection"}
                </Button>
              </DialogFooter>
            </>
          )}

          {panelMode === 'cancel-hr' && (
            <>
              <div className="py-2">
                <p className="text-sm text-muted-foreground mb-2">This will void the completed exit request and notify the Employee and Line Manager.</p>
                <FloatField
                  label="Reason for Cancellation"
                  required
                  type="textarea"
                  value={cancelReason}
                  onChange={(v) => setCancelReason(v as string)}
                  placeholder="Explain the reason…"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closePanel}>Cancel</Button>
                <Button variant="destructive" onClick={() => cancelHrMutation.mutate()} disabled={cancelHrMutation.isPending}>
                  {cancelHrMutation.isPending ? t("common.saving") : "Confirm Cancellation"}
                </Button>
              </DialogFooter>
            </>
          )}

          {panelMode === 'edit-dates' && (
            <>
              <div className="py-2">
                <FloatWrapper label={t("offboardingModule.actualLastDay")} required>
                  <DatePicker value={actualLastDay} onChange={setActualLastDay} className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm" />
                </FloatWrapper>
                <FloatField
                  label={t("offboardingModule.hrNotes")}
                  type="textarea"
                  value={hrNotes}
                  onChange={(v) => setHrNotes(v as string)}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closePanel}>{t("common.cancel")}</Button>
                <Button onClick={() => editDatesMutation.mutate()} disabled={editDatesMutation.isPending || !actualLastDay} className="gradient-brand text-white">
                  {editDatesMutation.isPending ? t("common.saving") : "Save Changes"}
                </Button>
              </DialogFooter>
            </>
          )}

          {panelMode === 'finalize' && (
            <>
              <div className="space-y-6 py-4">
                <div className="space-y-4 p-4 rounded-lg border bg-muted/20">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      <Calculator className="h-4 w-4" /> Final Date Setup
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-muted-foreground">Original Request</label>
                      <p className="text-sm font-medium h-9 flex items-center">
                        {selectedRequest?.preferred_last_day && format(new Date(selectedRequest.preferred_last_day), "dd MMM yyyy")}
                      </p>
                    </div>
                    <div>
                      <FloatWrapper label={t("offboardingModule.actualLastDay")} required>
                        <DatePicker value={actualLastDay} onChange={setActualLastDay} className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm" />
                      </FloatWrapper>
                      <p className="text-[10px] text-muted-foreground mt-1">{t("offboardingModule.calculateDate")} (Adjust manually)</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="text-sm font-semibold">{t("offboardingModule.tasks")}</h3>
                  <div className="space-y-2 border rounded-lg p-3 bg-card">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="revoke-access"
                        checked={checklist.revokeAccess}
                        onCheckedChange={(c) => setChecklist(prev => ({ ...prev, revokeAccess: !!c }))}
                      />
                      <label htmlFor="revoke-access" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        {t("offboardingModule.revokeAccess")} (Email, IT Systems)
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="return-eq"
                        checked={checklist.returnEquipment}
                        onCheckedChange={(c) => setChecklist(prev => ({ ...prev, returnEquipment: !!c }))}
                      />
                      <label htmlFor="return-eq" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        {t("offboardingModule.returnEquipment")} (Laptop, Badges)
                      </label>
                    </div>
                  </div>
                </div>

                <FloatField
                  label={t("offboardingModule.hrNotes")}
                  type="textarea"
                  value={hrNotes}
                  onChange={(v) => setHrNotes(v as string)}
                  placeholder="…"
                />
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={closePanel} disabled={finalizeMutation.isPending}>
                  {t("common.cancel")}
                </Button>
                <div className="flex gap-2 w-full sm:w-auto mt-2 sm:mt-0">
                  <Button
                    variant="secondary"
                    onClick={() => finalizeMutation.mutate({ status: "active" })}
                    disabled={finalizeMutation.isPending || !actualLastDay}
                    title="Save progress without completing the flow"
                  >
                    {t("common.save")} (Active)
                  </Button>
                  <Button
                    onClick={() => finalizeMutation.mutate({ status: "completed" })}
                    disabled={finalizeMutation.isPending || !actualLastDay || !allChecked}
                    className="gradient-brand text-white"
                    title="Finish process. Disables employee and notifies IT."
                  >
                    {finalizeMutation.isPending ? t("common.saving") : t("offboardingModule.status.completed")}
                  </Button>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
